import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { FaCheck, FaFacebook, FaSpinner, FaTimes, FaInstagram } from "react-icons/fa";
import { FacebookAccountCard } from "./FacebookAccountCard";
import { useFacebook } from "../hooks/useFacebook";
import { useInstagramDiscovery } from "../hooks/useInstagramDiscovery";
import { InstagramSelectionAccount } from "../types";
import { MAX_INSTAGRAM_ACCOUNTS_VIA_FACEBOOK } from "@/config";
import { PrimaryActionButton } from "@/components/ui/PrimaryActionButton";
import { GlassPanel } from "@/components/ui/GlassPanel";
import { useTranslation } from "react-i18next";

interface FacebookSectionProps {
  onInstagramAccountsFound?: (accounts: InstagramSelectionAccount[]) => void;
}

export function FacebookSection({ onInstagramAccountsFound }: FacebookSectionProps) {
  const { t } = useTranslation();
  const facebook = useFacebook();
  const instagramDiscovery = useInstagramDiscovery();
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [showInstagramSelection, setShowInstagramSelection] = useState(false);

  // Handle Instagram account fetching
  const handleFetchInstagramAccounts = async () => {
    const result = await instagramDiscovery.fetchAvailableAccounts();
    if (result.success && result.accounts && result.accounts.length > 0) {
      setShowInstagramSelection(true);
      onInstagramAccountsFound?.(result.accounts);
    }
  };

  // Handle account selection
  const handleAccountSelection = (accountId: string) => {
    setSelectedAccountIds(prev => {
      if (prev.includes(accountId)) {
        return prev.filter(id => id !== accountId);
      } else {
        if (prev.length < MAX_INSTAGRAM_ACCOUNTS_VIA_FACEBOOK) {
          return [...prev, accountId];
        } else {
          return [accountId];
        }
      }
    });
  };

  // Handle account confirmation
  const handleSelectAccounts = async () => {
    if (selectedAccountIds.length === 0 || !facebook.connection.profile) return;
    
    const selectedAccount = instagramDiscovery.availableAccounts.find(
      acc => acc.id === selectedAccountIds[0]
    );
    if (!selectedAccount) return;

    const result = await instagramDiscovery.selectAccount(
      selectedAccountIds[0], 
      selectedAccount.facebook_connection_id
    );

    if (result.success) {
      setShowInstagramSelection(false);
      setSelectedAccountIds([]);
      instagramDiscovery.reset();
    }
  };

  const handleRetryFetch = () => {
    handleFetchInstagramAccounts();
  };

  const handleCancelSelection = () => {
    setShowInstagramSelection(false);
    setSelectedAccountIds([]);
    instagramDiscovery.reset();
  };

  if (facebook.isLoadingFacebook) {
    return (
      <GlassPanel className="p-6">
        <h2 className="text-xl  mb-4">Facebook Connection</h2>
        <div className="animate-pulse">
          <div className="h-4 bg-white/10 rounded w-3/4 mb-4"></div>
          <div className="h-8 bg-white/10 rounded w-1/2"></div>
        </div>
      </GlassPanel>
    );
  }

  // Show Instagram account selection if triggered
  if (showInstagramSelection) {
    return (
      <GlassPanel className="p-6">
        <div className="text-center mb-6">
          <div className="mb-4">
            <h2 className="text-2xl  mb-2">
              {t("account.select_account_title")}
            </h2>
            <p className="">
              {t("account.select_account_description")}
            </p>
          </div>
        </div>

        {instagramDiscovery.isLoadingAccounts ? (
          <div className="text-center py-8">
            <FaSpinner className="text-4xl  animate-spin mx-auto mb-4" />
            <p className="">{t("account.processing")}</p>
          </div>
        ) : instagramDiscovery.availableAccounts.length === 0 ? (
          <div className="text-center py-8">
            <FaInstagram className=" text-4xl mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {t("account.no_professional_accounts_found")}
            </h3>
            <p className="mb-4">
              {t("account.no_professional_accounts_description")}
            </p>
            
            <div className="flex flex-col items-center space-y-3">
              <Button 
                onClick={handleRetryFetch}
                disabled={instagramDiscovery.isLoadingAccounts}
                variant="outline"
                className="flex items-center gap-2"
              >
                {instagramDiscovery.isLoadingAccounts ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    {t("account.retrying")}
                  </>
                ) : (
                  <>
                    <FaInstagram />
                    {t("account.try_again")}
                  </>
                )}
              </Button>
              
              <Button 
                onClick={handleCancelSelection}
                variant="outline"
                className="flex items-center gap-2"
              >
                <FaTimes />
                {t("cancel")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 max-w-2xl mx-auto">
            <h3 className="text-lg font-medium  text-center">
              Available Instagram Accounts ({instagramDiscovery.availableAccounts.length})
            </h3>
            
            <div className="space-y-3">
              {instagramDiscovery.availableAccounts.map((account) => (
                <div
                  key={account.id}
                  className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                    selectedAccountIds.includes(account.id)
                      ? 'border-neutral-500 bg-neutral-100 dark:bg-neutral-800'
                      : 'border-neutral-200 hover:border-neutral-300'
                  }`}
                  onClick={() => handleAccountSelection(account.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-12 h-12 rounded-full overflow-hidden bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mr-12">
                        {account.profile_picture_url ? (
                          <img
                            src={account.profile_picture_url}
                            alt={account.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <FaInstagram className="text-white text-lg" />
                        )}
                      </div>
                      <div>
                        <h4 className="">{account.name}</h4>
                        <p className="text-sm">@{account.username}</p>
                      </div>
                    </div>
                    
                    {selectedAccountIds.includes(account.id) && (
                      <FaCheck className="text-xl" />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-center mt-6">
              <PrimaryActionButton
                onClick={handleSelectAccounts}
                disabled={selectedAccountIds.length === 0}
                isLoading={instagramDiscovery.isSelecting}
                loadingText={t("account.processing")}
                size="lg"
              >
                {t("account.continue")}
              </PrimaryActionButton>
            </div>
          </div>
        )}
      </GlassPanel>
    );
  }

  return (
    <GlassPanel className="p-6">
      <h2 className="text-xl  mb-4">Facebook Connection</h2>
      
      {facebook.connection.isConnected && facebook.connection.profile ? (
        <FacebookAccountCard
          profile={facebook.connection.profile}
          isLoading={facebook.isConnecting}
          onDisconnect={facebook.disconnectFacebook}
          onFetchInstagramAccounts={handleFetchInstagramAccounts}
          isFetchingInstagramAccounts={instagramDiscovery.isLoadingAccounts}
        />
      ) : (
        <div className="text-center py-8">
          <FaFacebook className="mx-auto h-12 w-12 mb-4" />
          <h3 className="text-lg font-medium mb-2">{t("account.facebook_connect")}</h3>
          <p className="mb-6">
            {t("account.facebook_connect_subtitle")}
          </p>
          <Button
            onClick={facebook.connectFacebook}
            disabled={facebook.isConnecting}
            className="bg-neutral-800 hover:bg-neutral-700 text-white"
          >
            {facebook.isConnecting ? (
              <>
                <FaSpinner className="animate-spin mr-2" />
                {t("account.connecting")}
              </>
            ) : (
              <>
                <FaFacebook className="mr-2" />
                {t("account.facebook_connect")}
              </>
            )}
          </Button>
        </div>
      )}

      {facebook.facebookError && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{facebook.facebookError}</p>
        </div>
      )}
    </GlassPanel>
  );
} 